import { LngLatLike, RasterLayerSpecification } from 'mapbox-gl';

export type RadarRegion = 'de' | 'benelux' | 'eu' | 'es' | 'fr' | 'ch';

export interface RadarZoomPoint {
  searchName: string;
  longitude?: number | string;
  latitude?: number | string;
}

export const RADAR_DEFAULT_CENTER: LngLatLike = [9.8, 51.5];

export const RADAR_ZOOM_LEVEL_FOR_REGION: Record<RadarRegion | 'city', number> =
  {
    de: 5,
    benelux: 6,
    eu: 2,
    es: 5,
    fr: 5,
    ch: 5,
    city: 7,
  };

export const RADAR_PROJECTIONS: Record<
  RadarRegion,
  [number, number, number, number]
> = {
  de: [3.51175, 46.959255, 17.381118, 55.600819],
  benelux: [0, 48.8833, 9, 55.9667],
  es: [-9, 29, 4, 43],
  fr: [-9.965, 37.4602971, 17.5597105, 54.1840311],
  eu: [-14.993, 41.4678541, 20.4357948, 60.0106635],
  ch: [3.51175, 46.959255, 17.381118, 55.600819],
};

export const isRadarRegion = (region: string): region is RadarRegion => {
  return Object.prototype.hasOwnProperty.call(RADAR_PROJECTIONS, region);
};

export const getRadarProjection = (
  region: string
): [number, number, number, number] | null => {
  return isRadarRegion(region) ? RADAR_PROJECTIONS[region] : null;
};

export const getRadarZoomLevel = (
  region: string,
  zoomPoint: RadarZoomPoint | null | undefined
): number => {
  if (zoomPoint?.searchName !== 'whole Area') {
    return RADAR_ZOOM_LEVEL_FOR_REGION.city;
  }

  return isRadarRegion(region)
    ? RADAR_ZOOM_LEVEL_FOR_REGION[region]
    : RADAR_ZOOM_LEVEL_FOR_REGION.de;
};

export const getRadarZoomPointCoordinates = (
  zoomPoint: RadarZoomPoint | null | undefined
): LngLatLike => {
  const longitude = Number(zoomPoint?.longitude);
  const latitude = Number(zoomPoint?.latitude);

  if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
    return [longitude, latitude];
  }

  return RADAR_DEFAULT_CENTER;
};

export const createRasterInvisiblePaint =
  (): RasterLayerSpecification['paint'] => ({
    'raster-opacity': 0,
  });

export const createRasterVisiblePaint =
  (): RasterLayerSpecification['paint'] => ({
    'raster-opacity': 0.8,
    'raster-fade-duration': 0,
    'raster-opacity-transition': { delay: 0, duration: 0 },
  });

export interface RadarViewportState {
  center: LngLatLike;
  zoomLevel: number;
}

export interface RadarCirclePaint {
  'line-color': string;
  'line-width': number;
  'line-dasharray': number[];
}

export const getRadarViewportState = (
  region: string,
  zoomPoint: RadarZoomPoint | null | undefined
): RadarViewportState => {
  return {
    center: getRadarZoomPointCoordinates(zoomPoint),
    zoomLevel: getRadarZoomLevel(region, zoomPoint),
  };
};

export const createRadarCirclePaint = (
  styleName: string
): RadarCirclePaint => ({
  'line-color': styleName === 'street' ? 'rgba(88, 88, 88, 1)' : 'white',
  'line-width': 1,
  'line-dasharray': [2, 2],
});
